import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UserTopbar from "../UserTopbar";
import { useNavigate, useOutletContext } from "react-router-dom";
import UserSection from "../../Services/User/UserLogin";
import Swal from "sweetalert2";
import { deleteDraft } from "../../redux/slices/draftSlice";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../utils/Loader";
import { fetchUserInventory, fetchUserProduct } from "../../redux/slices/productSlice";
import { Select as MuiSelect } from "@mui/material";
import Select from "react-select";
import UserService from "../../Services/UserList";
import { Search } from "lucide-react";
import { FaSearch } from "react-icons/fa";
import Apiurl from "../../Helpers/apiUrl";

const UserInventory = () => {
  const { isSidebarOpen } = useOutletContext();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { inventory, count, getStatus } = useSelector((state) => state.product);
  const navigate = useNavigate();

  const counts = count || 0;

  useEffect(() => {
    dispatch(
      fetchUserInventory({
        search: searchQuery,
        page: page + 1,
        limit: rowsPerPage,
      })
    );
  }, [searchQuery, page, rowsPerPage, dispatch]);

  useEffect(() => {
    fetch(`${Apiurl}system-settings`, { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        if (data.status && data.inventory_menu === 0) {
          navigate("/user");
        }
      })
      .catch(err => console.error(err));
  }, [navigate]);

  const formatStockValue = (num) => {
    if (num === 0) return "0";
    if (!num || isNaN(num)) return "-";
    const val = Math.abs(num);
    if (val >= 10000000) return (num / 10000000).toFixed(1) + " Cr";
    if (val >= 100000) return (num / 100000).toFixed(1) + " L";
    if (val >= 1000) return (num / 1000).toFixed(1) + " K";
    return num.toString();
  };

  const getMonthLabels = () => {
    const now = new Date();
    const f = new Intl.DateTimeFormat("en-US", {
      month: "short",
      year: "numeric",
    });
    return {
      currentMonth: f.format(new Date(now.getFullYear(), now.getMonth(), 1)),
      nextMonth: f.format(new Date(now.getFullYear(), now.getMonth() + 1, 1)),
    };
  };

  const { currentMonth, nextMonth } = getMonthLabels();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  return (
    <>
      {getStatus === 'pending' && <Loader />}

      <UserTopbar heading="Inventory" sidebar={isSidebarOpen} />
      <div className="  p-2 shadow-xl mx-auto">
        <Box
          className="bg-gray-900/90 p-3"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTopRightRadius: 10,
            borderTopLeftRadius: 10,
            borderColor: "#fff",
          }}
        >
          <div className="relative">
            <input
              type="text"
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              placeholder="Search by SKU, Description"
              className="p-2 w-[27rem] border-2 border-teal-800 rounded-md text-black transition duration-200"
              required
            />
            <span className="absolute inset-y-0 right-0 flex items-center pr-3">
              <FaSearch className="text-teal-800" />
            </span>
          </div>
        </Box>

        <div className="rounded-sm shadow-lg overflow-x-auto">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  className="bg-gray-900/90"
                  sx={{
                    "& th": {
                      color: "#ffffff",
                      fontSize: "11px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      borderRight: "1px solid #1e293b",
                      borderBottom: "1px solid #1e293b",
                      position: "sticky",
                      top: 0,
                      zIndex: 11,
                    },
                  }}
                >
                  <TableCell rowSpan={2} align="center" sx={{ width: "80px" }}>
                    SL No.
                  </TableCell>
                  <TableCell rowSpan={2} sx={{ minWidth: "120px" }}>
                    SKU Code
                  </TableCell>
                  <TableCell rowSpan={2} sx={{ minWidth: "200px" }}>
                    Description
                  </TableCell>
                  <TableCell rowSpan={2} sx={{ minWidth: "100px" }}>
                    Preferences
                  </TableCell>
                  <TableCell rowSpan={2} align="center">
                    Indicator
                  </TableCell>
                  <TableCell rowSpan={2} align="center">

                    Available
                  </TableCell>
                  <TableCell rowSpan={2} align="center">

                    Status
                  </TableCell>
                  <TableCell rowSpan={2} align="center">

                    Pruning Date
                  </TableCell>
                  <TableCell rowSpan={2} align="center">
                    Updated On
                  </TableCell>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{
                      borderBottom: "2px solid #38bdf8 !important",
                    }}
                  >
                    {currentMonth}
                  </TableCell>
                  <TableCell
                    colSpan={4}
                    align="center"

                    sx={{
                      borderBottom: "2px solid #94a3b8 !important",
                    }}
                  >
                    {nextMonth}
                  </TableCell>
                </TableRow>

                <TableRow
                  className="bg-gray-900/90"
                  sx={{
                    "& th": {
                      color: "#94a3b8",
                      fontSize: "10px",
                      fontWeight: 800,
                      borderRight: "1px solid #1e293b",
                      position: "sticky",
                      top: "33.5px",
                      zIndex: 10,
                    },
                  }}
                >
                  {["WK1", "WK2", "WK3", "WK4", "WK1", "WK2", "WK3", "WK4"].map(
                    (wk, i) => (
                      <TableCell key={i} align="center">
                        {wk}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {inventory?.map((row, index) => (
                  <TableRow
                    key={row?.sku_code || index}
                    hover
                    sx={{
                      backgroundColor: "#fcfcfc",
                      "& td": {
                        fontSize: "12px",
                        borderRight: "1px solid #f1f5f9",
                        borderBottom: "1px solid #f1f5f9",
                        color: "#1e293b",
                        py: 1.2,
                      },
                      "&:hover": { backgroundColor: "#f1f5f9 !important" },
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{ color: "#64748b", fontWeight: 500 }}
                    >
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#0f172a",
                        fontFamily: "monospace",
                      }}
                    >
                      {row?.sku_code || "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#475569",
                        width: "280px",
                        minWidth: "200px",
                        lineHeight: "1.4",
                        fontSize: "11px",
                        fontWeight: 500,
                        verticalAlign: "top",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        py: 1.5,
                      }}
                    >
                      {row?.product?.product_desc || "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "#0f172a",
                        fontFamily: "monospace",
                      }}
                    >
                      {row?.product?.preferences || "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 1,
                          py: 0.25,
                          borderRadius: "4px",
                          fontSize: "10px",
                          fontWeight: 800,
                          bgcolor: "#f1f5f9",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        {row?.stockIndicator || "—"}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 800,
                        bgcolor: "#fff7ed",
                        color: "#c2410c",
                      }}
                    >
                      {formatStockValue(row?.stockAvailable)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 800,
                        bgcolor: "#fff7ed",
                        color: "#c2410c",
                      }}
                    >
                      {row?.status}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 800,
                        bgcolor: "#fff7ed",
                        color: "#c2410c",
                      }}
                    >
                      {row?.pruning_date ? row?.pruning_date : "—"}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: "#fff7ed",
                        color: "#c2410c",
                      }}
                    >
                      {new Date(row?.updated_at).toLocaleDateString('en-GB') || "—"}
                    </TableCell>

                    {[row?.cwk_1, row?.cwk_2, row?.cwk_3, row?.cwk_4].map(
                      (val, i) => (
                        <TableCell
                          key={`c-${i}`}
                          align="center"
                          sx={{
                            color: val > 0 ? "#15803d" : "#cbd5e1",
                            fontWeight: val > 0 ? 700 : 400,
                          }}
                        >
                          {formatStockValue(val)}
                        </TableCell>
                      )
                    )}

                    {[row?.nwk_1, row?.nwk_2, row?.nwk_3, row?.nwk_4].map(
                      (val, i) => (
                        <TableCell
                          key={`n-${i}`}
                          align="center"
                          sx={{
                            color: val > 0 ? "#15803d" : "#cbd5e1",
                            bgcolor: "#f8fafc",
                            fontWeight: val > 0 ? 700 : 400,
                          }}
                        >
                          {formatStockValue(val)}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            sx={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
            rowsPerPageOptions={[10, 50, 100, 500, counts]}
            component="div"
            count={counts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </div>
    </>
  );
};

export default UserInventory;
